import { Component } from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

/**
 * Global error boundary. Converts an uncaught render error (which would
 * otherwise unmount the whole React tree -> blank white screen) into a
 * recoverable fallback, and surfaces the actual error for diagnostics.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Visible in the browser console so the exact error/stack can be captured
    // (e.g. for device-specific crashes that don't reproduce on the desktop).
    console.error("[BeSca] UI-Fehler abgefangen:", error, info?.componentStack);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const message =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      "Unbekannter Fehler";
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background p-6"
        data-testid="error-boundary"
      >
        <div className="w-full max-w-md border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-primary/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-primary" />
            </div>
            <h1 className="font-heading text-xl font-medium">Etwas ist schiefgelaufen</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Die Aktion konnte nicht abgeschlossen werden. Bitte laden Sie die Seite neu.
            Ihre bereits gespeicherten Daten bleiben erhalten.
          </p>
          <pre
            className="text-xs bg-muted text-muted-foreground p-3 overflow-auto max-h-32 whitespace-pre-wrap break-words"
            data-testid="error-boundary-message"
          >
            {message}
          </pre>
          <button
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity w-full justify-center"
            data-testid="error-boundary-reload"
          >
            <RotateCw className="h-4 w-4" /> Seite neu laden
          </button>
        </div>
      </div>
    );
  }
}
